import { z } from "zod";
import { AttachmentId, Custodian, LocalDate, TaskId } from "../Types";
import { TASK_TITLE_MAX_LENGTH } from "./constants/common";
// Create the TypeInfo schema with a simpler structure
// Using a specific approach to avoid TypeScript circular reference errors
export const TypeInfo = z.lazy(() => z.object({
    typeId: z.number(),
    name: z.string(),
    displayName: z.string().optional(),
    cancelable: z.boolean().optional(),
    isSystem: z.boolean().optional(),
    subtypes: z.union([z.array(z.lazy(() => TypeInfo)), z.null()]).optional(),
}));
export const TemplateStatus = z.enum([
    "PRERELEASE",
    "ACTIVE",
    "DEPRECATED",
    "DELETED",
]);
export const ExprValue = z.union([
    z.object({ value: z.unknown() }).catchall(z.unknown()),
    z.object({ ref: z.string() }).catchall(z.unknown()),
]);
export const Expr = z.object({
    function: z.string(),
    params: z.record(z.string(), ExprValue).optional(),
});
export const ValidatorExpr = Expr.extend({
    errorMessage: z.string().optional(),
});
export const DisplayParams = z.object({
    type: z.string(),
    primaryLabel: z.string(),
});
export const FieldDisplayParams = DisplayParams.extend({
    secondaryLabel: z.string().optional(),
    placeholderText: z.string().optional(),
    infoBubbleText: z.string().optional(),
    helperText: z.string().optional(),
    required: z.union([z.boolean(), z.array(Expr)]).optional(),
    visible: z.union([z.boolean(), z.array(Expr)]).optional(),
    enabled: z.union([z.boolean(), z.array(Expr)]).optional(),
});
export const SelectFieldParams = FieldDisplayParams.extend({
    type: z.literal("SelectField"),
    multi: z.boolean().optional(),
    searchable: z.boolean(),
    addNewOption: z.boolean(),
});
export const InputFieldParams = FieldDisplayParams.extend({
    type: z.literal("InputField"),
    multiline: z.boolean(),
    fullWidth: z.boolean().optional(),
});
export const SelectAccountFieldParams = SelectFieldParams.extend({
    type: z.literal("SelectAccountField"),
});
export const DueDateInputFieldParams = InputFieldParams.extend({
    type: z.literal("DueDateInputField"),
});
export const RadioInputParams = FieldDisplayParams.extend({
    type: z.literal("RadioButtonField"),
});
export const ResourceType = z.object({
    type: z.string(),
    kinds: z.union([z.array(z.string()), z.string()]).optional(),
    params: z.record(z.string(), ExprValue).optional(),
});
export const DataType = z.object({
    type: z.string(),
    validation: z.array(ValidatorExpr).optional(),
    defaultValue: z.union([z.unknown(), Expr]).optional(),
});
export const EnumData = DataType.extend({
    type: z.literal("EnumData"),
    resourceType: ResourceType,
    params: z.record(z.string(), ExprValue).optional(),
});
export const PrimitiveData = DataType.extend({
    type: z.literal("PrimitiveData"),
    format: z.enum([
        "String",
        "Number",
        "DollarAmount",
        "Date",
        "SSN",
        "PhoneNumber",
        "Percentage",
    ]),
});
export const FieldParameter = z.object({
    key: z.string(),
    display: z.union([
        SelectAccountFieldParams,
        DueDateInputFieldParams,
        SelectFieldParams,
        RadioInputParams,
        InputFieldParams,
        FieldDisplayParams,
        DisplayParams,
    ]),
    determines: z.array(z.string()).optional(),
    determinedBy: z.array(z.string()).optional(),
    dataType: z.union([EnumData, PrimitiveData]).optional(),
});
export const TemplateInfo = z.object({
    templateId: z.string(),
    status: TemplateStatus,
    parameters: z.array(FieldParameter),
});
export const EnumValue = z.object({
    id: z.string(),
    displayValue: z.string(),
});
export const SimpleResourceValue = z.object({
    type: z.string(),
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
});
export const ResourceValueWithKind = SimpleResourceValue.extend({
    kind: z.union([z.array(z.string()), z.string(), z.null()]),
});
export const ParameterValue = z.union([
    EnumValue,
    ResourceValueWithKind,
    SimpleResourceValue,
    z.string(),
]);
export const ParameterValues = z.object({
    values: z.array(ParameterValue),
});
export const TaskManipulatorType = z.enum(["HUMAN", "SYSTEM"]);
export const TaskHumanManipulator = z.object({
    type: z.literal(TaskManipulatorType.enum.HUMAN),
    id: z.string(),
});
export const TaskSystemManipulator = z.object({
    type: z.literal(TaskManipulatorType.enum.SYSTEM),
});
export const TaskManipulator = z.discriminatedUnion("type", [
    TaskHumanManipulator,
    TaskSystemManipulator,
]);
export const TaskAttachment = z.object({
    id: AttachmentId,
    filename: z.string(),
    previewUrl: z.string().url(),
    downloadUrl: z.string().url(),
});
export const TaskTitle = z.string().max(TASK_TITLE_MAX_LENGTH);
export const TaskBasicInfo = z.object({
    id: TaskId,
    title: TaskTitle,
    taskStatus: z.string(),
    client: ResourceValueWithKind.nullable(),
});
export const Task = z.object({
    id: TaskId,
    fields: z.object({
        title: TaskTitle,
        description: z.string().nullish(),
        dueDate: LocalDate.nullable(),
        taskPriority: EnumValue,
        taskStatus: EnumValue,
        taskType: ResourceValueWithKind,
        serviceTeamId: ResourceValueWithKind.nullish(),
        assignee: ResourceValueWithKind.nullish(),
        advisor: ResourceValueWithKind,
        client: ResourceValueWithKind.nullish(),
    }),
    meta: z.object({
        creator: TaskManipulator,
        modifiedBy: TaskManipulator.nullable(),
        createdTs: z.string().datetime(),
        updatedTs: z.string().datetime(),
        completedTs: z.string().datetime().nullable(),
        isSystemGenerated: z.boolean(),
        isDeleted: z.boolean(),
    }),
    parent: TaskBasicInfo.nullable(),
    children: z.object({
        tasks: TaskBasicInfo.array(),
    }),
});
export const TaskDetails = Task.extend({
    template: z.object({
        id: TemplateInfo.shape.templateId,
    }),
    fields: Task.shape.fields.extend({
        custodian: Custodian.nullish(),
    }),
    attachments: TaskAttachment.array(),
});
// Account resource (special case with multiple kinds)
export const AccountResource = z.object({
    type: z.literal("Account"),
    kind: z.string(), // "External", "Farther", "ManuallyTracked", etc.
    value: z.string(),
});
// Person resource (Advisor, Client, etc.)
export const PersonResource = z.object({
    type: z.literal("Person"),
    kind: z.enum(["Advisor", "Client"]),
    value: z.string(),
});
// Service Team resource
export const ServiceTeamResource = z.object({
    type: z.literal("ServiceTeam"),
    value: z.string(),
});
// Task Type resource
export const TaskTypeResource = z.object({
    type: z.literal("TaskType"),
    value: z.string(),
});
// Union of all resource types
export const AnyResourceValue = z.union([
    AccountResource,
    PersonResource,
    ServiceTeamResource,
    TaskTypeResource,
]);
export const TaskFields = z.object({
    // Primitive fields
    title: z.string(),
    description: z.string().nullable().optional(),
    dueDate: z.string().nullable().optional(), // ISO date string (yyyy-MM-dd)
    custodian: z.string().nullable().optional(), // TextEnum value
    amount: z.string().optional(), // DollarAmount as string
    directFollowupAgreement: z.boolean().optional(),
    // Resource fields
    taskType: TaskTypeResource,
    serviceTeamId: ServiceTeamResource.nullable().optional(),
    advisor: PersonResource,
    client: PersonResource.nullable().optional(),
    assignee: AnyResourceValue.nullable().optional(), // Can be Person or ServiceTeam
    // Enum fields with id/displayValue structure
    taskStatus: EnumValue,
    taskPriority: EnumValue,
    // Account fields (for money movement tasks)
    fromAccount: AccountResource.optional(),
    toAccount: AccountResource.optional(),
});
// Template reference for requests
export const TemplateRef = z.object({
    id: z.string(),
});
// POST request payload
export const PostTaskRequest = z.object({
    template: TemplateRef,
    parentId: z.string().nullable().optional(),
    fields: TaskFields,
});
// PATCH request payload
export const PatchTaskRequest = z.object({
    template: TemplateRef,
    fields: TaskFields.partial(), // Allow partial updates for PATCH
});
// Update parent request payload
export const UpdateTaskParentRequest = z.object({
    parentId: z.string().nullable(),
});
// Validation error response
export const ValidationError = z.object({
    errors: z.object({
        global: z.array(z.string()).optional(),
        fields: z.record(z.string(), z.array(z.string())).optional(),
    }),
});
//# sourceMappingURL=Types.js.map